import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { type VirtualizedColumnDef } from "@tellescope/ui";
import { useAppPermissionQuery } from "../permissions";
import { type SortingState } from "@tanstack/react-table";
import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { createKeyMap } from "@tellescope/utils";
import { TableViewColumn } from "@tellescope/types-models";

export interface TableViewLocalOverrides<TFilters extends {} = Record<string, unknown>> {
  columnOrderOverride: string[] | undefined;
  setColumnOrderOverride: Dispatch<SetStateAction<string[] | undefined>>;
  filtersOverride: TFilters | undefined;
  setFiltersOverride: Dispatch<SetStateAction<TFilters | undefined>>;
  sortingOverride: SortingState | undefined;
  setSortingOverride: Dispatch<SetStateAction<SortingState | undefined>>;
}

/**
 * Manages local-only staged overrides for a table view (used when the user lacks
 * `update` permission for `table_views`). All overrides reset whenever `viewId`
 * changes. Lift this hook to a parent if you need the staged values to flow into
 * sibling hooks (e.g. `useTableView`'s column construction).
 */
export const useTableViewLocalOverrides = <TFilters extends {} = Record<string, unknown>>(
  viewId: string
): TableViewLocalOverrides<TFilters> => {
  const [columnOrderOverride, setColumnOrderOverride] = useState<string[] | undefined>(undefined);
  const [filtersOverride, setFiltersOverride] = useState<TFilters | undefined>(undefined);
  const [sortingOverride, setSortingOverride] = useState<SortingState | undefined>(undefined);
  const [trackedViewId, setTrackedViewId] = useState(viewId);

  if (trackedViewId !== viewId) {
    setTrackedViewId(viewId);
    setColumnOrderOverride(undefined);
    setFiltersOverride(undefined);
    setSortingOverride(undefined);
  }

  return {
    columnOrderOverride,
    setColumnOrderOverride,
    filtersOverride,
    setFiltersOverride,
    sortingOverride,
    setSortingOverride,
  };
};

export interface UseTableViewConfigArgs<TFilters extends {} = Record<string, unknown>> {
  /** Whether the selected view is the local default (not a DB-persisted view) */
  isDefaultView: boolean;
  userId: string;
  selectedView: TellescopeModelFromName<"table_views">;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: VirtualizedColumnDef<any>[];
  allColumnConfigMap: Record<string, { label?: string }>;
  defaultView: TellescopeModelFromName<"table_views">;
  setUserDefaultView: (
    userId: string,
    newVal: Parameters<Dispatch<SetStateAction<TellescopeModelFromName<"table_views">>>>[0]
  ) => void;
  activeFilters: TFilters;
  countActiveFilters: (filters: TFilters) => number;
  /**
   * Optional externally-managed override state. When provided, staged changes
   * are stored here so the parent can also pass them into `useTableView` /
   * downstream queries. When omitted, the hook falls back to internal state.
   */
  localOverrides?: TableViewLocalOverrides<TFilters>;
}

export interface UseTableViewConfigResult<TFilters extends {} = Record<string, unknown>> {
  columnVisibility: Record<string, boolean>;
  orderedVisibleColumnIds: string[];
  isDefaultViewModified: boolean;
  activeFiltersCount: number;
  isDefaultView: boolean;
  currentUserId: string;
  onApplyConfig: (orderedVisibleIds: string[], filters: TFilters, sorting?: SortingState) => void;
  resetDefaultView: () => void;
  combinedFieldToColumnConfigMap: Record<string, { label?: string }>;
  activeFilters: TFilters;
  /** Current effective sorting state (derived from view or local override). */
  sorting: SortingState;
  /** Sorting change handler that auto-persists to the view. Use this instead of raw setSorting. */
  onSortingChange: Dispatch<SetStateAction<SortingState>>;
  /** Whether any local override (columns, filters, or sorting) is currently staged. */
  hasLocalOverride: boolean;
  /** Clears all local overrides so the view reverts to its persisted state. */
  resetLocalOverride: () => void;
}

export function useTableViewConfig<TFilters extends {} = Record<string, unknown>>({
  isDefaultView,
  userId,
  selectedView,
  columns,
  allColumnConfigMap,
  defaultView,
  setUserDefaultView,
  activeFilters,
  countActiveFilters,
  localOverrides: providedOverrides,
}: UseTableViewConfigArgs<TFilters>): UseTableViewConfigResult<TFilters> {
  const updateTableViewMutation = useTellescopeMutation({
    model: "table_views",
    method: "updateOne",
  });

  const {
    update: { access: canUpdateTableViews },
  } = useAppPermissionQuery({ model: "table_views", actions: ["update"] });

  // Always allocate internal overrides so React's hook order is stable; ignored
  // when the parent supplies its own controller via `providedOverrides`.
  const internalOverrides = useTableViewLocalOverrides<TFilters>(selectedView.id);
  const {
    columnOrderOverride: localColumnOrderOverride,
    setColumnOrderOverride: setLocalColumnOrderOverride,
    filtersOverride: localFiltersOverride,
    setFiltersOverride: setLocalFiltersOverride,
    sortingOverride: localSortingOverride,
    setSortingOverride: setLocalSortingOverride,
  } = providedOverrides ?? internalOverrides;

  // ---------------------------------------------------------------------------
  // Sorting state: derived from the view's persisted filter, with a local
  // override for immediate UI response on column-header clicks.
  // ---------------------------------------------------------------------------

  /** Sorting persisted in the view's filter field. */
  const viewSorting = useMemo((): SortingState => {
    const filterSorting = (selectedView.filter as Record<string, unknown> | undefined)?.sorting;
    return Array.isArray(filterSorting) ? (filterSorting as SortingState) : [];
  }, [selectedView.filter]);

  /** The effective sorting state: local override (if user interacted) or persisted view sorting. */
  const sorting = localSortingOverride ?? viewSorting;

  /** Ref so that handleApplyConfig always reads the latest sorting without needing it as a dep. */
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;

  // ---------------------------------------------------------------------------
  // Column visibility & ordering
  // ---------------------------------------------------------------------------

  const orderedVisibleColumnIds = useMemo<string[]>(() => {
    const orderedIds = localColumnOrderOverride
      ? localColumnOrderOverride.filter((id) => id !== "index")
      : selectedView.columns.reduce<string[]>((acc, col) => {
          if (col.field !== "index") {
            acc.push(col.field);
          }
          return acc;
        }, []);

    const actionsIndex = orderedIds.indexOf("Actions");
    if (actionsIndex !== -1) {
      const withoutActions = orderedIds.filter((id) => id !== "Actions");
      return [...withoutActions, "Actions"];
    }
    return orderedIds;
  }, [selectedView.columns, localColumnOrderOverride]);

  const columnVisibility = useMemo<Record<string, boolean>>(() => {
    const visibility: Record<string, boolean> = {};

    Object.keys(allColumnConfigMap).forEach((id) => {
      visibility[id] = false;
    });

    if (localColumnOrderOverride) {
      localColumnOrderOverride.forEach((id) => {
        if (id !== "index") visibility[id] = true;
      });
    } else {
      columns.forEach((col) => {
        if (col.id) visibility[col.id] = true;
      });
    }

    return visibility;
  }, [columns, allColumnConfigMap, localColumnOrderOverride]);

  const activeFiltersCount = useMemo(() => countActiveFilters(activeFilters), [activeFilters, countActiveFilters]);

  const isDefaultViewModified = useMemo(() => {
    if (!isDefaultView) return false;
    const currentColumns = selectedView.columns.map((col) => col.field);
    const defaultColumns = defaultView.columns.map((col) => col.field);
    if (currentColumns.length !== defaultColumns.length) return true;
    if (currentColumns.some((field, i) => field !== defaultColumns[i])) return true;
    if (sorting.length > 0) return true;
    return !!activeFiltersCount;
  }, [isDefaultView, selectedView.columns, defaultView.columns, activeFiltersCount, sorting]);

  // ---------------------------------------------------------------------------
  // Apply config (columns + filters). Uses ref to always include the latest sorting.
  // ---------------------------------------------------------------------------

  const handleApplyConfig = useCallback(
    (orderedVisibleIds: string[], filters: TFilters = {} as TFilters, newSorting?: SortingState) => {
      const effectiveSorting = newSorting ?? sortingRef.current;

      // If sorting was explicitly provided, update the local override & ref so
      // subsequent reads (before the next render) see the new value.
      if (newSorting) {
        setLocalSortingOverride(newSorting);
        sortingRef.current = newSorting;
      }

      const filterWithSorting = { ...filters, ...(effectiveSorting.length ? { sorting: effectiveSorting } : {}) };

      if (isDefaultView) {
        setUserDefaultView(userId, (currentView) => {
          const columnByField = createKeyMap(currentView.columns, "field");
          const indexCol = columnByField["index"];

          const newColumns = orderedVisibleIds.reduce<TableViewColumn[]>((acc, id) => {
            if (id === "index") return acc;
            if (columnByField[id]) {
              acc.push(columnByField[id]);
              return acc;
            }
            acc.push({ field: id, type: "Built-In" as const });
            return acc;
          }, []);

          return {
            ...currentView,
            columns: [...newColumns, ...(indexCol ? [indexCol] : [])],
            filter: filterWithSorting,
          };
        });
      } else if (canUpdateTableViews) {
        const columnByField = createKeyMap(selectedView.columns, "field");
        const indexCol = columnByField["index"];

        const newColumns = orderedVisibleIds.reduce<TableViewColumn[]>((acc, id) => {
          if (id === "index") return acc;
          if (columnByField[id]) {
            acc.push(columnByField[id]);
            return acc;
          }
          acc.push({ field: id, type: "Built-In" as const });
          return acc;
        }, []);

        updateTableViewMutation.mutate({
          id: selectedView.id,
          columns: [...newColumns, ...(indexCol ? [indexCol] : [])],
          filter: filterWithSorting,
          _options: { replaceObjectFields: true },
        });
      } else {
        setLocalColumnOrderOverride(orderedVisibleIds.filter((id) => id !== "index"));
        setLocalFiltersOverride(filters);
      }
    },
    [
      isDefaultView,
      canUpdateTableViews,
      setLocalSortingOverride,
      setUserDefaultView,
      userId,
      selectedView.columns,
      selectedView.id,
      updateTableViewMutation,
      setLocalColumnOrderOverride,
      setLocalFiltersOverride,
    ]
  );

  // ---------------------------------------------------------------------------
  // Sorting change handler — auto-persists on column header click.
  // ---------------------------------------------------------------------------

  /** Persists the new sorting state to the view (auto-persist on column header click). */
  const onSortingChange = useCallback<Dispatch<SetStateAction<SortingState>>>(
    (newSorting) => {
      const resolvedSorting = newSorting instanceof Function ? newSorting(sortingRef.current) : newSorting;

      // Update local override for immediate UI response
      setLocalSortingOverride(resolvedSorting);

      // Persist sorting to the view
      const filterWithSorting = {
        ...activeFilters,
        ...(resolvedSorting.length ? { sorting: resolvedSorting } : {}),
      };

      if (isDefaultView) {
        setUserDefaultView(userId, (currentView) => ({
          ...currentView,
          filter: filterWithSorting,
        }));
      } else if (canUpdateTableViews) {
        updateTableViewMutation.mutate({
          id: selectedView.id,
          filter: filterWithSorting,
          _options: { replaceObjectFields: true },
        });
      }
    },
    [
      setLocalSortingOverride,
      activeFilters,
      isDefaultView,
      canUpdateTableViews,
      setUserDefaultView,
      userId,
      updateTableViewMutation,
      selectedView.id,
    ]
  );

  const resetDefaultView = useCallback(() => {
    setLocalSortingOverride(undefined);
    setLocalFiltersOverride(undefined);
    setLocalColumnOrderOverride(undefined);
    setUserDefaultView(userId, defaultView);
  }, [
    setLocalSortingOverride,
    setLocalFiltersOverride,
    setLocalColumnOrderOverride,
    setUserDefaultView,
    userId,
    defaultView,
  ]);

  const resetLocalOverride = useCallback(() => {
    setLocalSortingOverride(undefined);
    setLocalFiltersOverride(undefined);
    setLocalColumnOrderOverride(undefined);
  }, [setLocalSortingOverride, setLocalFiltersOverride, setLocalColumnOrderOverride]);

  const hasLocalOverride =
    localColumnOrderOverride !== undefined || localFiltersOverride !== undefined || localSortingOverride !== undefined;

  return {
    columnVisibility,
    orderedVisibleColumnIds,
    isDefaultViewModified,
    activeFiltersCount,
    isDefaultView,
    currentUserId: userId,
    onApplyConfig: handleApplyConfig,
    resetDefaultView,
    combinedFieldToColumnConfigMap: allColumnConfigMap,
    activeFilters: localFiltersOverride ?? activeFilters,
    sorting,
    onSortingChange,
    hasLocalOverride,
    resetLocalOverride,
  };
}
